import * as React from "react";
import { IOnboardingFolder, IOnboardingTooltipItem } from "../../../../models/onboarding-tooltip";
import OnboardingTooltipService from "../../../../services/onboarding-tooltip-service";
import { queryClient } from "../../../../services/query-client";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { AnimatedIconType, OnboardingAnimatedIcon } from "../../../onboarding-tooltip/onboarding-animated-icon";
import { onboardingTooltipKeys } from "../../../onboarding-tooltip/onboarding-tooltip-query";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH6 from "../../../ui-kit/typography/H6/H6";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import { OnboardingFolderModal } from "./onboarding-folder-modal";
import "./onboarding-panel.scss";
import { OnboardingTooltipModal } from "./onboarding-tooltip-modal";

export interface IDeleteConfirmation {
  type: "folder" | "tooltip";
  id: string;
  name: string;
}

export interface IOnboardingPanelState {
  tooltips: IOnboardingTooltipItem[];
  folders: IOnboardingFolder[];
  searchQuery: string;
  selectedFolderId?: string;
  sortBy: "name" | "createdAt";
  sortOrder: "asc" | "desc";
  draftFilter: "all" | "draft" | "published";
  triggerTypeFilter: "all" | "hover" | "click" | "auto" | "hint" | "always";
  isTooltipModalOpen: boolean;
  isFolderModalOpen: boolean;
  editingTooltip?: IOnboardingTooltipItem;
  editingFolder?: IOnboardingFolder;
  draggedTooltipId?: string;
  dragOverFolderId?: string;
  isDeleteModalOpen: boolean;
  itemToDelete?: IDeleteConfirmation;
  duplicatingTooltipId?: string;
  /** Where the delete confirmation stands. Owned by `SaveStatus`, never set directly. */
  deleteStatus: KuikaButtonStatus;
}

/** A delete is deleting, not saving. */
const DELETE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Deleting", success: "Deleted" };

export interface IOnboardingPanelProps {}

class OnboardingPanel extends React.PureComponent<
  IOnboardingPanelProps & IWithContextProp & IWithHistoryProp,
  IOnboardingPanelState
> {
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IOnboardingPanelProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      tooltips: [],
      folders: [],
      searchQuery: "",
      selectedFolderId: undefined,
      sortBy: "createdAt",
      sortOrder: "desc",
      draftFilter: "all",
      triggerTypeFilter: "all",
      isTooltipModalOpen: false,
      isFolderModalOpen: false,
      editingTooltip: undefined,
      editingFolder: undefined,
      draggedTooltipId: undefined,
      dragOverFolderId: undefined,
      isDeleteModalOpen: false,
      itemToDelete: undefined,
      duplicatingTooltipId: undefined,
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount(): void {
    this.deleteStatus.dispose();
  }

  // Drops the designer's cached tooltip read so an edit here shows up without a reload.
  refreshDesignerTooltips = (): void => {
    void queryClient.invalidateQueries(onboardingTooltipKeys.all);
  };

  // Every tooltip write reloads this list and drops the designer's cached read, once. The
  // create/edit dialog does the second half itself (refreshOnboardingTooltips).
  reloadAfterMutation = (): void => {
    this.refreshDesignerTooltips();
    void this.loadData();
  };

  loadData = async () => {
    let tooltips: IOnboardingTooltipItem[];
    try {
      tooltips = await OnboardingTooltipService.getTooltips();
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      return;
    }
    const folders = await OnboardingTooltipService.getFolders();

    const normalizedFolders = folders.map((folder) => ({
      ...folder,
      parentId: folder.parentId === null ? undefined : folder.parentId
    }));

    this.setState({ tooltips, folders: normalizedFolders });
  };

  handleSearchChange = (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    this.setState({ searchQuery: String(value) });
  };

  handleSortChange = (value: string | number | Array<string | number> | null) => {
    if (typeof value === "string") {
      const [sortBy, sortOrder] = value.split("-") as ["name" | "createdAt", "asc" | "desc"];
      this.setState({ sortBy, sortOrder });
    }
  };

  handleDraftFilterChange = (value: string | number | Array<string | number> | null) => {
    if (typeof value === "string") {
      this.setState({ draftFilter: value as "all" | "draft" | "published" });
    }
  };

  handleTriggerTypeFilterChange = (value: string | number | Array<string | number> | null) => {
    if (typeof value === "string") {
      this.setState({ triggerTypeFilter: value as "all" | "hover" | "click" | "auto" | "hint" | "always" });
    }
  };

  handleToggleDraft = async (tooltip: IOnboardingTooltipItem) => {
    await OnboardingTooltipService.toggleDraft(tooltip.id);
    this.reloadAfterMutation();
  };

  handleDuplicateTooltip = async (tooltip: IOnboardingTooltipItem) => {
    this.setState({ duplicatingTooltipId: tooltip.id });
    try {
      await OnboardingTooltipService.duplicateTooltip(tooltip.id);
      this.reloadAfterMutation();
    } finally {
      this.setState({ duplicatingTooltipId: undefined });
    }
  };

  handleFolderSelect = (folderId?: string) => {
    this.setState({ selectedFolderId: folderId });
  };

  handleOpenTooltipModal = (tooltip?: IOnboardingTooltipItem) => {
    this.setState({ isTooltipModalOpen: true, editingTooltip: tooltip });
  };

  handleCloseTooltipModal = () => {
    this.setState({ isTooltipModalOpen: false, editingTooltip: undefined });
    this.loadData();
  };

  handleOpenFolderModal = (folder?: IOnboardingFolder) => {
    this.setState({ isFolderModalOpen: true, editingFolder: folder });
  };

  handleCloseFolderModal = () => {
    this.setState({ isFolderModalOpen: false, editingFolder: undefined });
    this.loadData();
  };

  handleOpenDeleteModal = (type: "folder" | "tooltip", id: string, name: string) => {
    this.setState({
      isDeleteModalOpen: true,
      itemToDelete: { type, id, name }
    });
  };

  handleCloseDeleteModal = () => {
    // During the ✓ hold the confirmation is already on its way out; closing then would cancel the
    // reload that shows the item gone.
    if (this.deleteStatus.current === "success") return;
    this.setState({
      isDeleteModalOpen: false,
      itemToDelete: undefined
    });
  };

  handleConfirmDelete = async () => {
    const { itemToDelete } = this.state;
    if (!itemToDelete) return;
    // A second press while the delete runs or its ✓ is held would delete twice.
    if (!this.deleteStatus.canStart) return;

    this.deleteStatus.start();
    try {
      if (itemToDelete.type === "folder") {
        await OnboardingTooltipService.deleteFolder(itemToDelete.id);
      } else {
        await OnboardingTooltipService.deleteTooltip(itemToDelete.id);
      }
    } catch {
      // Before, a refused delete left the confirmation open without a word; the button says so now.
      this.deleteStatus.fail();
      return;
    }

    // The ✓ goes up first; the confirmation leaves after it and the list reloads without the item.
    this.deleteStatus.succeed((): void => {
      this.setState({
        isDeleteModalOpen: false,
        itemToDelete: undefined
      });
      this.reloadAfterMutation();
    });
  };

  handleDragStart = (e: React.DragEvent, tooltipId: string) => {
    const dragTarget = e.currentTarget.parentElement;
    if (dragTarget) {
      e.dataTransfer.setDragImage(dragTarget, 0, 0);
    }
    this.setState({ draggedTooltipId: tooltipId });
  };

  handleDragEnd = () => {
    this.setState({ draggedTooltipId: undefined, dragOverFolderId: undefined });
  };

  handleDragOver = (e: React.DragEvent, folderId?: string) => {
    e.preventDefault();
    this.setState({ dragOverFolderId: folderId });
  };

  handleDrop = async (e: React.DragEvent, folderId?: string) => {
    e.preventDefault();
    const { draggedTooltipId } = this.state;
    if (draggedTooltipId) {
      const tooltip = this.state.tooltips.find((t) => t.id === draggedTooltipId);
      if (tooltip) {
        await OnboardingTooltipService.saveTooltip({ ...tooltip, folderId });
        this.reloadAfterMutation();
      }
    }
    this.setState({ draggedTooltipId: undefined, dragOverFolderId: undefined });
  };

  getFilteredTooltips = (): IOnboardingTooltipItem[] => {
    const { tooltips, searchQuery, selectedFolderId, sortBy, sortOrder, draftFilter, triggerTypeFilter } = this.state;

    let filtered = tooltips;

    if (selectedFolderId !== undefined) {
      filtered = filtered.filter((t) => t.folderId === selectedFolderId);
    } else {
      filtered = filtered.filter((t) => !t.folderId);
    }

    if (draftFilter !== "all") {
      filtered = filtered.filter((t) => {
        if (draftFilter === "draft") return t.isDraft;
        if (draftFilter === "published") return !t.isDraft;
        return true;
      });
    }

    if (triggerTypeFilter !== "all") {
      filtered = filtered.filter((t) => t.triggerType === triggerTypeFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name?.toLowerCase().includes(query) ||
          t.selector?.toLowerCase().includes(query) ||
          t.label?.toLowerCase().includes(query) ||
          t.header?.toLowerCase().includes(query)
      );
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "name":
          comparison = (a.name || a.selector || "").localeCompare(b.name || b.selector || "");
          break;
        case "createdAt":
          comparison = a.createdAt - b.createdAt;
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  };

  renderFolderItem = (folder: IOnboardingFolder, level: number = 0): React.ReactNode => {
    const { folders, selectedFolderId, dragOverFolderId } = this.state;
    const isSelected = selectedFolderId === folder.id;
    const isDragOver = dragOverFolderId === folder.id;
    const tooltipCount = this.state.tooltips.filter((t) => t.folderId === folder.id).length;

    const leftIconContent = (
      <div className="onboarding-panel__folder-left-icon">
        <span className="onboarding-panel__folder-expand-placeholder" />
        <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />
      </div>
    );

    const rightContent = (
      <div className="onboarding-panel__folder-right">
        <div className="onboarding-panel__folder-actions">
          <KuikaButton
            type="text"
            isIcon
            onClick={(e) => {
              e?.stopPropagation();
              this.handleOpenFolderModal(folder);
            }}
            aria-label="Edit folder"
            tabIndex={0}
          >
            <KuikaSvg name="edit" width={14} height={14} />
          </KuikaButton>
          <KuikaButton
            type="text"
            isIcon
            color="danger"
            onClick={(e) => {
              e?.stopPropagation();
              this.handleOpenDeleteModal("folder", folder.id, folder.name);
            }}
            aria-label="Delete folder"
            tabIndex={0}
          >
            <KuikaSvg name="trash" width={14} height={14} />
          </KuikaButton>
        </div>
        <span className="onboarding-panel__folder-count">{tooltipCount}</span>
      </div>
    );

    const hasChildren = folders.some((f) => f.parentId === folder.id);

    if (hasChildren) {
      const childFolders = folders.filter((f) => f.parentId === folder.id).sort((a, b) => a.order - b.order);

      const collapseHeaderClassName = [
        "onboarding-panel__folder-collapse-header",
        isSelected ? "onboarding-panel__folder-collapse-header--selected" : "",
        isDragOver ? "onboarding-panel__folder-collapse-header--drag-over" : ""
      ]
        .filter(Boolean)
        .join(" ");

      const collapseHeader = (
        <div
          className={collapseHeaderClassName}
          onClick={(e) => {
            e.stopPropagation();
            this.handleFolderSelect(folder.id);
          }}
          onDragOver={(e: React.DragEvent) => this.handleDragOver(e, folder.id)}
          onDrop={(e: React.DragEvent) => this.handleDrop(e, folder.id)}
          role="button"
          tabIndex={0}
          aria-label={`Select folder ${folder.name}`}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              this.handleFolderSelect(folder.id);
            }
          }}
        >
          <div className="onboarding-panel__folder-collapse-title">
            <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />
            <span>{folder.name}</span>
          </div>
          {rightContent}
        </div>
      );

      const collapseItems = [
        {
          key: folder.id,
          label: collapseHeader,
          children: (
            <div className="onboarding-panel__folder-children" style={{ paddingLeft: `${(level + 1) * 8}px` }}>
              {childFolders.map((childFolder) => this.renderFolderItem(childFolder, level + 1))}
            </div>
          )
        }
      ];

      return (
        <div key={folder.id} className="onboarding-panel__folder-item onboarding-panel__folder-item--collapsible">
          <KuikaCollapsePanel
            items={collapseItems}
            defaultActiveKey={[folder.id]}
            className="onboarding-panel__folder-collapse"
          />
        </div>
      );
    }

    const menuItemClassName = [
      "onboarding-panel__folder-menu-item",
      isDragOver ? "onboarding-panel__folder-menu-item--drag-over" : ""
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div key={folder.id} className="onboarding-panel__folder-item">
        <KuikaMenuItem
          title={folder.name}
          leftIcon={leftIconContent}
          rightUnit={rightContent}
          selected={isSelected}
          className={menuItemClassName}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => this.handleFolderSelect(folder.id)}
          onDragOver={(e: React.DragEvent) => this.handleDragOver(e, folder.id)}
          onDrop={(e: React.DragEvent) => this.handleDrop(e, folder.id)}
          tabIndex={0}
          aria-label={`Select folder ${folder.name}`}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              this.handleFolderSelect(folder.id);
            }
          }}
        />
      </div>
    );
  };

  renderFolderTree = (parentId?: string, level: number = 0): React.ReactNode => {
    const { folders } = this.state;
    const childFolders = folders.filter((f) => f.parentId === parentId).sort((a, b) => a.order - b.order);

    return childFolders.map((folder) => this.renderFolderItem(folder, level));
  };

  renderTooltipItem = (tooltip: IOnboardingTooltipItem): React.ReactNode => {
    const { draggedTooltipId } = this.state;
    const isDragging = draggedTooltipId === tooltip.id;

    return (
      <div
        key={tooltip.id}
        data-testid="onboardingTooltipRow"
        className={`onboarding-panel__tooltip-item ${isDragging ? "onboarding-panel__tooltip-item--dragging" : ""}`}
        role="listitem"
        style={{
          borderColor: tooltip.isDraft ? "var(--yellow-5)" : "var(--dark-3)"
        }}
      >
        <div
          className="onboarding-panel__tooltip-drag"
          draggable
          onDragStart={(e) => this.handleDragStart(e, tooltip.id)}
          onDragEnd={this.handleDragEnd}
        >
          <KuikaSvg name="grip-vertical" width={16} height={16} />
        </div>
        <div
          className="onboarding-panel__tooltip-content"
          onClick={() => this.handleOpenTooltipModal(tooltip)}
          style={{ cursor: "pointer" }}
        >
          <div className="onboarding-panel__tooltip-header">
            <KuikaH6 data-testid="onboardingTooltipRowTitle">{tooltip.name || tooltip.selector}</KuikaH6>
            <div className="onboarding-panel__tooltip-badges">
              {tooltip.isDraft && <span className="onboarding-panel__badge onboarding-panel__badge--draft">DRAFT</span>}
              {tooltip.useCssSelector && (
                <span className="onboarding-panel__badge onboarding-panel__badge--css-selector">CSS</span>
              )}
              <span
                data-testid="onboardingTooltipTriggerBadge"
                className={`onboarding-panel__badge onboarding-panel__badge--${tooltip.triggerType}`}
              >
                {tooltip.triggerType}
              </span>
              <span
                data-testid="onboardingTooltipPositionBadge"
                className="onboarding-panel__badge onboarding-panel__badge--position"
              >
                {tooltip.position}
              </span>
              {tooltip.steps && tooltip.steps.length > 0 && (
                <span className="onboarding-panel__badge onboarding-panel__badge--steps">
                  {tooltip.steps.length} steps
                </span>
              )}
              {tooltip.icon && (
                <span className="onboarding-panel__badge onboarding-panel__badge--icon">
                  {tooltip.icon.startsWith("animated-") ? (
                    <OnboardingAnimatedIcon
                      type={tooltip.icon.replace("animated-", "") as AnimatedIconType}
                      size={12}
                      color="currentColor"
                    />
                  ) : (
                    <KuikaSvg name={tooltip.icon} type="common" width={12} height={12} />
                  )}
                  icon
                </span>
              )}
            </div>
          </div>
          <div className="onboarding-panel__tooltip-preview">
            {tooltip.selector && (
              <KuikaSmallText className="onboarding-panel__tooltip-preview-label">{tooltip.selector}</KuikaSmallText>
            )}
            {tooltip.header && (
              <KuikaSmallText className="onboarding-panel__tooltip-preview-header">{tooltip.header}</KuikaSmallText>
            )}
            {tooltip.label && (
              <KuikaSmallText className="onboarding-panel__tooltip-preview-label">{tooltip.label}</KuikaSmallText>
            )}
          </div>
        </div>
        <div className="onboarding-panel__tooltip-actions">
          <KuikaButton
            type="text"
            isIcon
            data-testid="onboardingTooltipPublishToggle"
            onClick={(e) => {
              e?.stopPropagation();
              this.handleToggleDraft(tooltip);
            }}
            aria-label={tooltip.isDraft ? "Publish tooltip" : "Unpublish tooltip"}
            tabIndex={0}
          >
            <KuikaSvg name={tooltip.isDraft ? "eye" : "eye-off"} width={16} height={16} />
          </KuikaButton>
          <KuikaButton
            type="text"
            isIcon
            onClick={(e) => {
              e?.stopPropagation();
              this.handleDuplicateTooltip(tooltip);
            }}
            aria-label="Duplicate tooltip"
            tabIndex={0}
            disabled={this.state.duplicatingTooltipId === tooltip.id}
          >
            <KuikaSvg name="copy" width={16} height={16} />
          </KuikaButton>
          <KuikaButton
            type="text"
            isIcon
            color="danger"
            data-testid="onboardingTooltipDeleteButton"
            onClick={(e) => {
              e?.stopPropagation();
              this.handleOpenDeleteModal("tooltip", tooltip.id, tooltip.name || tooltip.selector || "Tooltip");
            }}
            aria-label="Delete tooltip"
            tabIndex={0}
          >
            <KuikaSvg name="trash" width={16} height={16} />
          </KuikaButton>
        </div>
      </div>
    );
  };

  handleClearAllFilters = () => {
    this.setState({
      searchQuery: "",
      draftFilter: "all",
      triggerTypeFilter: "all",
      sortBy: "createdAt",
      sortOrder: "desc"
    });
  };

  getActiveFilterCount = (): number => {
    const { draftFilter, triggerTypeFilter, searchQuery } = this.state;
    let count = 0;
    if (draftFilter !== "all") count++;
    if (triggerTypeFilter !== "all") count++;
    if (searchQuery.trim()) count++;
    return count;
  };

  renderDeleteConfirmationModal = (): React.ReactNode => {
    const { isDeleteModalOpen, itemToDelete } = this.state;
    if (!isDeleteModalOpen || !itemToDelete) return null;

    const itemType = itemToDelete.type === "folder" ? "Folder" : "Tooltip";

    const footer = (
      <KModalFooter
        onCancel={this.handleCloseDeleteModal}
        submitLabel="Delete"
        submitTone="danger"
        submitTestId="onboardingDeleteConfirmButton"
        onSubmit={this.handleConfirmDelete}
        status={this.state.deleteStatus}
        statusLabels={DELETE_STATUS_LABELS}
      />
    );

    return (
      <KModal
        showModal={isDeleteModalOpen}
        closeModal={this.handleCloseDeleteModal}
        header={`Delete ${itemType}`}
        footer={footer}
        width={400}
        centered
      >
        <div className="onboarding-panel__delete-modal">
          <KuikaModalIconWrapper iconName="trash" intent="destructive" />
          <div className="onboarding-panel__delete-modal-content">
            <KuikaText>Are you sure you want to delete "{itemToDelete.name}"?</KuikaText>
            <KuikaSmallText className="onboarding-panel__delete-modal-warning">
              This action cannot be undone.
            </KuikaSmallText>
          </div>
        </div>
      </KModal>
    );
  };

  render() {
    const {
      selectedFolderId,
      isTooltipModalOpen,
      isFolderModalOpen,
      editingTooltip,
      editingFolder,
      searchQuery,
      sortBy,
      sortOrder,
      draftFilter,
      triggerTypeFilter,
      dragOverFolderId
    } = this.state;
    const filteredTooltips = this.getFilteredTooltips();
    const rootTooltipCount = this.state.tooltips.filter((t) => !t.folderId).length;

    return (
      <KuikaSpinnerSmall spinning={!!this.state.duplicatingTooltipId}>
        <div className="onboarding-panel">
          <div className="onboarding-panel__sidebar">
            <div className="onboarding-panel__sidebar-header">
              <KuikaText className="onboarding-panel__sidebar-title">Folders</KuikaText>
              <KuikaButton
                type="text"
                isIcon
                onClick={() => this.handleOpenFolderModal()}
                aria-label="Add folder"
                tabIndex={0}
              >
                <KuikaSvg name="plus" width={16} height={16} />
              </KuikaButton>
            </div>
            <div className="onboarding-panel__folder-list">
              <KuikaMenuItem
                title="Uncategorized"
                leftIcon={
                  <div className="onboarding-panel__folder-left-icon">
                    <span className="onboarding-panel__folder-expand-placeholder" />
                  </div>
                }
                rightUnit={<span className="onboarding-panel__folder-count">{rootTooltipCount}</span>}
                selected={selectedFolderId === undefined}
                className={`onboarding-panel__folder-menu-item onboarding-panel__folder-menu-item--root`}
                onClick={() => this.handleFolderSelect(undefined)}
                onDragOver={(e: React.DragEvent) => this.handleDragOver(e, undefined)}
                onDrop={(e: React.DragEvent) => this.handleDrop(e, undefined)}
                tabIndex={0}
                aria-label="Select all tooltips"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    this.handleFolderSelect(undefined);
                  }
                }}
              />
              {this.renderFolderTree()}
            </div>
          </div>

          <div className="onboarding-panel__main">
            <div className="onboarding-panel__toolbar">
              <div className="onboarding-panel__toolbar-top">
                <div className="onboarding-panel__search">
                  <KuikaInput
                    placeholder="Search by name, selector, header or label..."
                    value={searchQuery}
                    onChange={this.handleSearchChange}
                    leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                    rightIcon={
                      searchQuery ? (
                        <KuikaButton
                          type="text"
                          isIcon
                          onClick={() => this.setState({ searchQuery: "" })}
                          aria-label="Clear search"
                        >
                          <KuikaSvg name="x" width={14} height={14} />
                        </KuikaButton>
                      ) : undefined
                    }
                    data-testid="onboardingSearchInput"
                    data-onboarding-id="onboarding-panel_search"
                  />
                </div>
                <KuikaButton
                  type="primary"
                  onClick={() => this.handleOpenTooltipModal()}
                  data-testid="onboardingAddTooltipButton"
                  data-onboarding-id="onboarding-panel_add-tooltip"
                >
                  <KuikaSvg name="plus" width={14} height={14} />
                  ADD TOOLTIP
                </KuikaButton>
              </div>

              <div className="onboarding-panel__toolbar-bottom">
                <div className="onboarding-panel__filters">
                  <div className="onboarding-panel__filter-group">
                    <KuikaSmallText className="onboarding-panel__filter-label">
                      <KuikaSvg name="adjustments-horizontal" type="common" width={12} height={12} />
                      Filters
                    </KuikaSmallText>
                    <div className="onboarding-panel__filter-controls">
                      <KuikaSelectbox
                        value={draftFilter}
                        onChange={this.handleDraftFilterChange}
                        options={[
                          { value: "all", label: "All Status" },
                          { value: "draft", label: "Draft" },
                          { value: "published", label: "Published" }
                        ]}
                        className="onboarding-panel__filter-select"
                        placeholder="Status"
                        data-onboarding-id="onboarding-panel_status"
                      />
                      <KuikaSelectbox
                        value={triggerTypeFilter}
                        onChange={this.handleTriggerTypeFilterChange}
                        options={[
                          { value: "all", label: "All Triggers" },
                          { value: "hover", label: "Hover" },
                          { value: "click", label: "Click" },
                          { value: "hint", label: "Hint" },
                          { value: "always", label: "Always Visible" }
                        ]}
                        className="onboarding-panel__filter-select"
                        placeholder="Trigger Type"
                        data-onboarding-id="onboarding-panel_trigger-type"
                      />
                    </div>
                  </div>

                  <div className="onboarding-panel__divider" />

                  <div className="onboarding-panel__filter-group">
                    <KuikaSmallText className="onboarding-panel__filter-label">
                      <KuikaSvg name="arrows-horizontal" type="common" width={12} height={12} />
                      Sort By
                    </KuikaSmallText>
                    <div className="onboarding-panel__filter-controls">
                      <KuikaSelectbox
                        value={`${sortBy}-${sortOrder}`}
                        onChange={this.handleSortChange}
                        options={[
                          { value: "name-asc", label: "Name A → Z" },
                          { value: "name-desc", label: "Name Z → A" },
                          { value: "createdAt-desc", label: "Newest First" },
                          { value: "createdAt-asc", label: "Oldest First" }
                        ]}
                        className="onboarding-panel__sort-select"
                        data-onboarding-id="onboarding-panel_sort-by"
                      />
                    </div>
                  </div>
                </div>

                <div className="onboarding-panel__results-info">
                  <KuikaSmallText>
                    {filteredTooltips.length} {filteredTooltips.length === 1 ? "tooltip" : "tooltips"}
                  </KuikaSmallText>
                </div>
              </div>
            </div>

            <div className="onboarding-panel__content">
              {filteredTooltips.length === 0 ? (
                <KuikaEmptyState
                  svgName="info-circle"
                  customName={
                    this.getActiveFilterCount() > 0 || searchQuery
                      ? "No tooltips match your filters"
                      : "No tooltips found"
                  }
                  description={
                    this.getActiveFilterCount() > 0 || searchQuery
                      ? "Try adjusting your filters or search query"
                      : "Create a new tooltip to get started"
                  }
                />
              ) : (
                <div className="onboarding-panel__tooltip-list" role="list">
                  {filteredTooltips.map((tooltip) => this.renderTooltipItem(tooltip))}
                </div>
              )}
            </div>
          </div>

          {isTooltipModalOpen && (
            <OnboardingTooltipModal
              isOpen={isTooltipModalOpen}
              onClose={this.handleCloseTooltipModal}
              tooltip={editingTooltip}
              folderId={selectedFolderId}
              refreshOnboardingTooltips={this.refreshDesignerTooltips}
            />
          )}

          {isFolderModalOpen && (
            <OnboardingFolderModal
              isOpen={isFolderModalOpen}
              onClose={this.handleCloseFolderModal}
              folder={editingFolder}
            />
          )}

          {this.renderDeleteConfirmationModal()}
        </div>
      </KuikaSpinnerSmall>
    );
  }
}

export default withContext<IOnboardingPanelProps>(
  withHistory<IOnboardingPanelProps>(withManagement<IOnboardingPanelProps>(OnboardingPanel))
);
